import { useColorModeValue } from "@/components/ui/color-mode"
import { colors } from "@/theme"
import {
  Input as ChakraInput,
  type InputProps as ChakraInputProps,
} from "@chakra-ui/react"
import { forwardRef } from "react"

export interface VisionInputProps extends Omit<ChakraInputProps, "variant"> {
  variant?: "glass" | "outline" | "solid"
}

export const VisionInput = forwardRef<HTMLInputElement, VisionInputProps>(
  ({ variant = "glass", size = "lg", ...props }, ref) => {
    const isLight = useColorModeValue(true, false)

    const darkVariants = {
      glass: {
        bg: colors.glass.dark,
        border: "1px solid",
        borderColor: colors.glass.whiteAlpha[200],
        color: "white",
        backdropFilter: "blur(20px)",
        fontWeight: "500",
        _placeholder: { color: "gray.400", fontWeight: "400" },
        _hover: { borderColor: colors.glass.whiteAlpha[300] },
        _focus: {
          borderColor: colors.brand[200],
          boxShadow: `0 0 0 1px ${colors.brand[200]}`,
        },
        _invalid: { borderColor: "red.500", boxShadow: "none" },
      },
      outline: {
        bg: "transparent",
        border: "1px solid",
        borderColor: colors.glass.whiteAlpha[300],
        color: "white",
        fontWeight: "500",
        _placeholder: { color: "gray.400", fontWeight: "400" },
        _hover: { borderColor: colors.glass.whiteAlpha[400] },
        _focus: {
          borderColor: colors.brand[200],
          boxShadow: `0 0 0 1px ${colors.brand[200]}`,
        },
        _invalid: { borderColor: "red.500", boxShadow: "none" },
      },
      solid: {
        bg: "rgb(19,21,54)",
        border: "1px solid",
        borderColor: "transparent",
        color: "white",
        fontWeight: "500",
        _placeholder: { color: "gray.400", fontWeight: "400" },
        _hover: { borderColor: colors.glass.whiteAlpha[200] },
        _focus: {
          borderColor: colors.brand[200],
          boxShadow: `0 0 0 1px ${colors.brand[200]}`,
        },
        _invalid: { borderColor: "red.500", boxShadow: "none" },
      },
    }

    const lightVariants = {
      glass: {
        bg: "white",
        border: "1px solid",
        borderColor: "#E2E8F0",
        color: "#1a202c",
        backdropFilter: "none",
        fontWeight: "500",
        _placeholder: { color: "#A0AEC0", fontWeight: "400" },
        _hover: { borderColor: "#CBD5E0" },
        _focus: {
          borderColor: colors.brand[200],
          boxShadow: `0 0 0 1px ${colors.brand[200]}`,
        },
        _invalid: { borderColor: "red.500", boxShadow: "none" },
      },
      outline: {
        bg: "transparent",
        border: "1px solid",
        borderColor: "#CBD5E0",
        color: "#1a202c",
        fontWeight: "500",
        _placeholder: { color: "#A0AEC0", fontWeight: "400" },
        _hover: { borderColor: "#A0AEC0" },
        _focus: {
          borderColor: colors.brand[200],
          boxShadow: `0 0 0 1px ${colors.brand[200]}`,
        },
        _invalid: { borderColor: "red.500", boxShadow: "none" },
      },
      solid: {
        bg: "#EDF2F7",
        border: "1px solid",
        borderColor: "transparent",
        color: "#1a202c",
        fontWeight: "500",
        _placeholder: { color: "#A0AEC0", fontWeight: "400" },
        _hover: { borderColor: "#E2E8F0" },
        _focus: {
          borderColor: colors.brand[200],
          boxShadow: `0 0 0 1px ${colors.brand[200]}`,
        },
        _invalid: { borderColor: "red.500", boxShadow: "none" },
      },
    }

    const styles = isLight
      ? lightVariants[variant] || lightVariants.glass
      : darkVariants[variant] || darkVariants.glass

    const sizeStyles = {
      sm: { h: "40px", px: "16px", fontSize: "sm", borderRadius: "12px" },
      md: { h: "45px", px: "20px", fontSize: "sm", borderRadius: "15px" },
      lg: { h: "50px", px: "24px", fontSize: "md", borderRadius: "20px" },
    }

    const currentSizeStyles =
      sizeStyles[size as keyof typeof sizeStyles] || sizeStyles.lg

    return (
      <ChakraInput ref={ref} {...styles} {...currentSizeStyles} {...props} />
    )
  },
)

VisionInput.displayName = "VisionInput"
